import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, UploadCloud, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";

// Empareja el nombre de archivo con la unidad por VIN completo o últimos 6 caracteres
// (Fix C, 2026-09-01). Prioriza el VIN completo: es imposible que coincida por error.
function emparejarUnidad(nombreArchivo, unidades) {
  const base = nombreArchivo.replace(/\.[^/.]+$/, "").toUpperCase();
  const porCompleto = unidades.find((u) => base.includes(u.vin));
  if (porCompleto) return { unidad: porCompleto, motivo: null };
  const candidatos = unidades.filter((u) => base.includes(u.vin_last6));
  if (candidatos.length === 0) return { unidad: null, motivo: "VIN no encontrado en el nombre del archivo" };
  if (candidatos.length > 1) return { unidad: null, motivo: "VIN ambiguo: coincide con más de una unidad" };
  return { unidad: candidatos[0], motivo: null };
}

export default function ImportarFotos() {
  usePageTitle("Importar fotos");
  const navigate = useNavigate();
  const [cargando, setCargando] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [asignaciones, setAsignaciones] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [error, setError] = useState("");

  const procesarArchivos = async (files) => {
    setError("");
    setResumen(null);
    setCargando(true);
    try {
      const unidades = [];
      let page = 1;
      let total = Infinity;
      while (unidades.length < total) {
        const { data } = await adminApi.get("/admin/unidades", { params: { page, page_size: 100 } });
        total = data.total;
        unidades.push(...data.resultados.map((u) => ({ id: u.id, vin: u.vin, vin_last6: u.vin_last6 })));
        if (data.resultados.length === 0) break;
        page += 1;
      }

      const previas = Array.from(files).map((archivo) => {
        const { unidad, motivo } = emparejarUnidad(archivo.name, unidades);
        return { archivo, unidad, motivo, estado: "pendiente" };
      });

      const idsUnicos = [...new Set(previas.filter((p) => p.unidad).map((p) => p.unidad.id))];
      const conteos = {};
      for (const id of idsUnicos) {
        try {
          const { data } = await adminApi.get(`/admin/unidades/${id}`);
          conteos[id] = data.fotos.length;
        } catch {
          conteos[id] = 5;
        }
      }

      const asignadasPorUnidad = {};
      const finales = previas.map((p) => {
        if (!p.unidad) return p;
        const actuales = conteos[p.unidad.id] ?? 0;
        const yaAsignadas = asignadasPorUnidad[p.unidad.id] ?? 0;
        if (actuales + yaAsignadas >= 5) {
          return { ...p, unidad: null, motivo: "La unidad ya tiene 5 fotos" };
        }
        asignadasPorUnidad[p.unidad.id] = yaAsignadas + 1;
        return p;
      });

      setAsignaciones(finales);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCargando(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setArrastrando(false);
    if (e.dataTransfer.files?.length) procesarArchivos(e.dataTransfer.files);
  };

  const subirTodo = async () => {
    setSubiendo(true);
    setResumen(null);
    const nuevas = [...asignaciones];
    let ok = 0;
    let fallidas = 0;
    for (let i = 0; i < nuevas.length; i++) {
      if (!nuevas[i].unidad) continue;
      nuevas[i] = { ...nuevas[i], estado: "subiendo" };
      setAsignaciones([...nuevas]);
      try {
        const fd = new FormData();
        fd.append("archivo", nuevas[i].archivo);
        await adminApi.post(`/admin/unidades/${nuevas[i].unidad.id}/fotos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        nuevas[i] = { ...nuevas[i], estado: "ok" };
        ok += 1;
      } catch (err) {
        nuevas[i] = { ...nuevas[i], estado: "error", motivo: formatApiError(err) };
        fallidas += 1;
      }
      setAsignaciones([...nuevas]);
    }
    setSubiendo(false);
    const sinAsignar = nuevas.filter((n) => !n.unidad).length;
    setResumen({ ok, fallidas, sinAsignar });
    toast.success(`${ok} foto${ok === 1 ? "" : "s"} subida${ok === 1 ? "" : "s"}`);
  };

  const totalAsignadas = asignaciones.filter((a) => a.unidad).length;
  const totalSinAsignar = asignaciones.filter((a) => !a.unidad).length;
  const hechas = asignaciones.filter((a) => a.estado === "ok" || a.estado === "error").length;
  const progreso = totalAsignadas > 0 ? Math.round((hechas / totalAsignadas) * 100) : 0;

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <Link to="/admin/unidades" className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:text-[var(--tc-accent)]">
        <ArrowLeft size={15} /> Volver a unidades
      </Link>
      <h1 className="text-xl font-semibold text-[var(--tc-text)]">Importar fotos por VIN</h1>
      <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">
        Soltá muchas fotos juntas: cada archivo se asigna a la unidad cuyo VIN (completo o últimos 6 caracteres) aparezca en el nombre.
      </p>

      {error && (
        <p className="mt-4 rounded-[var(--tc-radius)] bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{error}</p>
      )}

      <div className="mt-6 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
        <div
          data-testid={ADMIN.importFotosDropzone}
          onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={onDrop}
          onClick={() => document.getElementById("import-fotos-input")?.click()}
          className={`flex flex-col items-center justify-center gap-2 rounded-[var(--tc-radius)] border-2 border-dashed p-10 text-center transition-colors ${
            arrastrando ? "cursor-pointer border-[var(--tc-accent)] bg-[var(--tc-accent-soft)]" : "cursor-pointer border-[var(--tc-border)] hover:border-[var(--tc-accent)]"
          }`}
        >
          <input
            data-testid={ADMIN.importFotosFileInput}
            id="import-fotos-input"
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => e.target.files?.length && procesarArchivos(e.target.files)}
          />
          {cargando ? (
            <>
              <Loader2 className="animate-spin text-[var(--tc-accent)]" size={24} />
              <p className="text-sm text-[var(--tc-text-secondary)]">Emparejando archivos con unidades...</p>
            </>
          ) : (
            <>
              <UploadCloud size={24} className="text-[var(--tc-accent)]" />
              <p className="text-sm text-[var(--tc-text-secondary)]">Arrastrá varias fotos acá o hacé clic para seleccionarlas</p>
              <p className="text-xs text-[var(--tc-text-secondary)]">El nombre del archivo debe incluir el VIN completo o sus últimos 6 caracteres</p>
            </>
          )}
        </div>
      </div>

      {asignaciones.length > 0 && (
        <div className="mt-5 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tc-accent-soft)] px-3 py-1.5 text-sm font-semibold text-[var(--tc-accent)]">
              <CheckCircle2 size={14} /> {totalAsignadas} asignada{totalAsignadas === 1 ? "" : "s"}
            </span>
            {totalSinAsignar > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-600">
                <AlertTriangle size={14} /> {totalSinAsignar} sin asignar
              </span>
            )}
          </div>

          <div className="mt-4 overflow-x-auto rounded-[var(--tc-radius)] border border-[var(--tc-border)]">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-[var(--tc-border)] text-left text-xs font-medium uppercase tracking-wide text-[var(--tc-text-secondary)]">
                  <th className="px-4 py-2.5">Archivo</th>
                  <th className="px-4 py-2.5">Unidad</th>
                  <th className="px-4 py-2.5">Estado</th>
                </tr>
              </thead>
              <tbody>
                {asignaciones.map((a, i) => (
                  <tr key={i} data-testid={ADMIN.importFotosPreviewRow} className="border-b border-[var(--tc-border)] last:border-0">
                    <td className="max-w-[220px] truncate px-4 py-2.5 text-[var(--tc-text)]">{a.archivo.name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-[var(--tc-text-secondary)]">{a.unidad ? a.unidad.vin : "—"}</td>
                    <td className="px-4 py-2.5">
                      {a.estado === "ok" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--tc-accent)]"><CheckCircle2 size={13} /> Subida</span>
                      ) : a.estado === "error" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600"><XCircle size={13} /> {a.motivo}</span>
                      ) : a.estado === "subiendo" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--tc-text-secondary)]"><Loader2 size={13} className="animate-spin" /> Subiendo...</span>
                      ) : a.unidad ? (
                        <span className="text-xs text-[var(--tc-text-secondary)]">Lista para subir</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600"><AlertTriangle size={13} /> {a.motivo}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {subiendo && (
            <div className="mt-4">
              <div data-testid={ADMIN.importFotosProgress} className="h-2 w-full overflow-hidden rounded-full bg-[var(--tc-surface)]">
                <div className="h-full rounded-full bg-[var(--tc-accent)] transition-all" style={{ width: `${progreso}%` }} />
              </div>
              <p className="mt-1.5 text-xs text-[var(--tc-text-secondary)]">{progreso}% · {hechas}/{totalAsignadas}</p>
            </div>
          )}

          {!resumen && (
            <button
              data-testid={ADMIN.importFotosUploadButton}
              onClick={subirTodo}
              disabled={totalAsignadas === 0 || subiendo}
              className="mt-5 inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:opacity-60"
            >
              {subiendo && <Loader2 size={16} className="animate-spin" />} Subir {totalAsignadas} foto{totalAsignadas === 1 ? "" : "s"}
            </button>
          )}

          {resumen && (
            <div data-testid={ADMIN.importFotosResultSummary} className="mt-5 rounded-[var(--tc-radius)] bg-[var(--tc-surface)] p-4 text-sm">
              <p className="font-medium text-[var(--tc-text)]">
                {resumen.ok} subida{resumen.ok === 1 ? "" : "s"}, {resumen.fallidas} fallida{resumen.fallidas === 1 ? "" : "s"}, {resumen.sinAsignar} sin asignar.
              </p>
              <button
                onClick={() => navigate("/admin/unidades")}
                className="mt-3 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--tc-accent-hover)]"
              >
                Volver a unidades
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
